import React, { useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { CloseIcon, HeartIcon, PhoneIcon } from '@/components/UI/Icons/Icons';
import { Button } from '@/components/UI/Button/Button';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { formatNumber } from '@/utils/helpers';
import * as S from '@/components/Ad/SliderWithGallery/SliderWithGallery.styles';
import Gallery, { GalleryRef } from '@/components/Gallery/Gallery';
import { Slider } from '@/components/Slider/Slider';
import {
  GALLERY_ROOT_STYLES,
  SLIDER_ID,
} from '@/components/Ad/SliderWithGallery/SliderWithGallery.constants';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { PhonesBottomSheet } from '@/components/PhonesBottomSheet/PhonesBottomSheet';
import { useOpenContacts } from '@/hooks/useOpenContacts';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { useFloatingButtonsVisibility } from '@/hooks/useFloatingButtonsVisibility';
import { AdStatus } from '@/types';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useServerDataContext } from '@/contexts';

interface SliderWithGalleryProps {
  isBookmarked?: boolean;
  toggleBookmark: () => Promise<void>;
  dataStat?: string;
  overlayText?: string;
  canBookmark?: boolean;
}

export const SliderWithGallery: React.FC<SliderWithGalleryProps> = ({
  isBookmarked,
  toggleBookmark,
  dataStat,
  overlayText,
  canBookmark,
}) => {
  const theme = useTheme();
  const { adDetails, loading } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const { id, title, status, kinds, price, photosPreview } =
    adDetails ?? initialAdDetails ?? {};
  const isAuthor = useIsAuthor();
  const { isCallsAvailable } = useFloatingButtonsVisibility();
  const { isContactsOpen, toggleContacts, getPhoneNumbers, phoneNumbers } =
    useOpenContacts();
  const [isGalleryOpen, toggleGallery] = useToggleOpenState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const galleryRef = useRef<GalleryRef>(null);

  useBodyOverflow(isGalleryOpen);

  function openGallery() {
    galleryRef.current?.open();
    toggleGallery(true);
  }

  function closeGallery() {
    galleryRef.current?.close();
    toggleGallery(false);
  }

  const images = useMemo(
    () => adDetails?.photos?.map(photo => photo.url),
    [adDetails?.photos]
  );

  const sliderImages = useMemo(
    () => photosPreview?.map(photo => photo.url),
    [photosPreview]
  );

  const rawPhotos = initialAdDetails?.photosPreview ?? adDetails?.photosPreview;
  const photos = Array.isArray(rawPhotos) ? rawPhotos : [];
  const firstSlide = photos[0]?.url ?? '';
  const isPlaceholder = firstSlide.includes('ad_i_placeholder');
  const isNoPhotos = isPlaceholder && photos.length === 1;

  const counter = `${currentIndex + 1} / ${images?.length}`;

  if (!initialAdDetails && !adDetails) {
    return <SkeletonLoader height="330px" />;
  }

  if (isNoPhotos) return null;

  return (
    <React.Fragment>
      {sliderImages?.length ? (
        <Slider
          loading={loading}
          title={title}
          kinds={kinds}
          images={sliderImages || []}
          openGallery={openGallery}
          sliderId={SLIDER_ID}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          dataStat={dataStat}
          overlayText={overlayText}
        />
      ) : loading ? (
        <SkeletonLoader height="330px" />
      ) : null}

      {isGalleryOpen && (
        <>
          <S.Head>
            <Button appearance="transparent" size="l" onClick={closeGallery}>
              <CloseIcon fill="white" />
            </Button>

            <S.CountGallery $variant="3" $isBig={!canBookmark}>
              {counter}
            </S.CountGallery>

            {canBookmark && (
              <Button
                appearance="transparent"
                size="l"
                onClick={toggleBookmark}
              >
                <HeartIcon
                  fill={isBookmarked ? theme.palette.brand.main : 'none'}
                  stroke={
                    isBookmarked
                      ? theme.palette.brand.main
                      : theme.palette.system.white
                  }
                />
              </Button>
            )}
          </S.Head>

          <S.Footer>
            <div>
              {price !== null && (
                <S.Price>{formatNumber(price, 2, true)} AZN</S.Price>
              )}

              <S.Description>{title}</S.Description>
            </div>

            {!isAuthor && status === AdStatus.Published && isCallsAvailable && (
              <S.CallButton onClick={() => getPhoneNumbers(id)}>
                <PhoneIcon />
              </S.CallButton>
            )}
          </S.Footer>

          <PhonesBottomSheet
            isOpen={isContactsOpen}
            close={toggleContacts}
            phones={phoneNumbers}
          />
        </>
      )}

      {images?.length ? (
        <Gallery
          images={images}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          ref={galleryRef}
          sliderId={SLIDER_ID}
          rootStyles={GALLERY_ROOT_STYLES}
          isSimpleView={false}
          onClose={closeGallery}
        />
      ) : null}
    </React.Fragment>
  );
};
